import { z } from 'zod';

/**
 * Every schema below mirrors `api/src/socialsrc_api/schemas/estimates.py`
 * field for field. That module is the contract; this file is its transcript.
 * A field here that is not there is a guess, and a field there that is not
 * here is silently dropped at the boundary.
 *
 * **Two deliberate departures from the plan's own illustrative listing,
 * found by parsing real server output against it (§6.6's worked tables,
 * generated straight from the real B2 estimator):**
 *
 * 1. `z.record(mediaKindSchema, ...)` in Zod 4 requires every enum member as
 *    a key -- confirmed empirically, `{ POST: 12 }` fails a
 *    `z.record(mediaKindSchema, z.number())` parse outright. The wire is
 *    genuinely partial: `EstimateOut.kinds` and `.tail` are plain Python
 *    dicts holding only the media kinds the account actually has configured
 *    (the `principal` fixture has no `REEL` entry anywhere; `supporter` has
 *    no lifecycle kind at all), and `AssumptionsOut.rate_per_day` is the
 *    same shape for the same reason. `z.partialRecord` is the correct
 *    transcription of a Python `dict[MediaKind, X]` that is not guaranteed
 *    complete -- it types `estimate.kinds.POST` as `KindOut | undefined`,
 *    which is the honest type: a caller reading a kind that was not
 *    configured must be forced to handle its absence, not hand-wave it away
 *    with a cast (§5.4, "absence is stored as absence, never as zero").
 * 2. `kindSchema` carries `comments_credits_per_day`, and it is not
 *    reconstructed here. It is a `@property` on core's `KindBreakdown`
 *    dataclass (`rate_per_day * comments.credits_per_item`), which is
 *    exactly why it went missing for a time: `KindOut.of`'s field-by-field
 *    construction only fails type checking when a *field* is forgotten, not
 *    a property, so the figure §6.6 calls the dominant cost lever silently
 *    never reached the wire. The fix is on the response model
 *    (`KindOut.comments_credits_per_day`, populated from the same
 *    property) -- this file still reconstructs nothing; it parses a number
 *    the server now actually sends.
 */

export const MEDIA_KINDS = ['POST', 'REEL', 'STORY'] as const;
export const mediaKindSchema = z.enum(MEDIA_KINDS);

export const pageLineSchema = z.object({
  page_index: z.number().int(),
  youngest_age_days: z.number(),
  tier_slug: z.string(),
  fetches_per_day: z.number(),
  credits_per_day: z.number(),
});

export const statsSchema = z.object({
  pages: z.array(pageLineSchema),
  credits_per_day: z.number(),
  // The DETECTION floor (§6.3.3): the newest page's own cost, independent of
  // posting rate. Never the same number as `commentsSchema.floor_credits`.
  floor_credits_per_day: z.number(),
});

export const commentSegmentSchema = z.object({
  tier_slug: z.string(),
  rounds: z.number(),
  comments_arriving: z.number(),
  comments_per_round: z.number(),
  pull_every_k_rounds: z.number().int(),
  pulls: z.number(),
  pages_per_pull: z.number().int(),
  credits: z.number(),
  floor_credits: z.number(),
  utilisation: z.number(),
});

export const commentsSchema = z.object({
  segments: z.array(commentSegmentSchema),
  credits_per_item: z.number(),
  // The BATCHING floor (§6.6): what the same comments would cost pulled
  // perfectly once. Not the same floor as `statsSchema.floor_credits_per_day`
  // -- nothing in this repository may add the two together.
  floor_credits: z.number(),
  // British spelling, matching core exactly.
  utilisation: z.number(),
  // null means the cap never bound. Never render it as -1 or as 0.
  capped_in_segment_index: z.number().int().nullable(),
});

export const inferenceLineSchema = z.object({
  // The estimator's own `inference_lines()` (core's `estimate_burn_rate.py`)
  // emits exactly these three tasks per kind; `comment_intelligence` replaced
  // `stance_comment` and the fourth `cluster_summary` line was dropped in
  // core 1ce73bf / 67473f3.
  task: z.enum(['profile_account', 'comment_intelligence', 'post_intelligence']),
  units_per_day: z.number(),
  input_tokens_per_day: z.number(),
  output_tokens_per_day: z.number(),
  provider_credits_per_day: z.number(),
});

/**
 * Every nullable field here is §5.4's absence, not a zero. An ephemeral kind
 * (STORY) has no rate and no comment line; a lifecycle kind (POST, REEL) has
 * no sweep count. See the module docstring's point 2 for
 * `comments_credits_per_day`.
 */
export const kindSchema = z.object({
  media_kind: mediaKindSchema,
  rate_per_day: z.number().nullable(),
  stats: statsSchema.nullable(),
  comments: commentsSchema.nullable(),
  sweeps_per_day: z.number().nullable(),
  story_credits_per_day: z.number().nullable(),
  /** Platform credits on the rate card; `null` when an operation has no rate. */
  comments_credits_per_day: z.number().nullable(),
  inference: z.array(inferenceLineSchema),
  provider_credits_per_day: z.number(),
  enrichment_credits_per_day: z.number(),
  credits_per_day: z.number().nullable(),
});

export const tailSchema = z.object({
  comments_per_item: z.number(),
  uncapped_credits_per_item: z.number(),
  capped_credits_per_item: z.number(),
  floor_credits_per_item: z.number(),
  cap: z.number().int().nullable(),
});

export const arrivalBandSchema = z.object({ until_hours: z.number(), share: z.number() });

export const tokenRatesSchema = z.object({
  stance_in: z.number().int(),
  stance_out: z.number().int(),
  profile_in: z.number().int(),
  profile_out: z.number().int(),
  post_in: z.number().int(),
  post_out: z.number().int(),
});

export const assumptionsSchema = z.object({
  // Partial: a fresh account with no lifecycle kinds configured (the
  // `supporter` fixture) reports `{}` here, not one entry per media kind.
  rate_per_day: z.partialRecord(mediaKindSchema, z.number()),
  comments_per_item: z.number(),
  tail_comments_per_item: z.number(),
  profiling_sample: z.number().int(),
  profile_fetch_price: z.number().int(),
  rates: tokenRatesSchema,
  curve: z.array(arrivalBandSchema),
  // §6.6: which inputs were measured from L1 and which fell back to a
  // default. `InputSource` has exactly three members. Keyed by the seven
  // names in core's `ASSUMPTION_INPUTS`, but parsed as an open record so an
  // input a later B2 task adds does not fail the parse -- an unrecognised
  // source value falls back to `'default'` rather than rejecting the whole
  // estimate over one forward-compatible field.
  sources: z.record(z.string(), z.enum(['measured', 'default', 'tenant']).catch('default')),
});

/**
 * `tail` is keyed by media kind, not a single object: §6.3.7 puts the cap per
 * kind, so one figure for the account would price a reel's tail against a
 * post's cap. There is no top-level `inference` on the wire -- the lines hang
 * under the kind that produced them.
 *
 * `kinds` and `tail` are partial for the same reason `assumptions.rate_per_day`
 * is: the server reports only the media kinds an account actually has
 * configured, and `tail` additionally omits any kind whose comments are off
 * (it is a comment-volume scenario) and every ephemeral kind (STORY never
 * pulls comments).
 */
export const estimateSchema = z.object({
  kinds: z.partialRecord(mediaKindSchema, kindSchema),
  tail: z.partialRecord(mediaKindSchema, tailSchema),
  assumptions: assumptionsSchema,
  statements: z.array(z.string()).length(4), // §6.6: always four
  provider_credits_per_day: z.number(),
  enrichment_credits_per_day: z.number(),
  /** Platform credits on the rate card; `null` while any operation has no rate. */
  credits_per_day: z.number().nullable(),
  credits_per_30d: z.number().nullable(),
  /** The operations with no rate. */
  unpriced: z.array(z.string()),
  input_tokens_per_day: z.number(),
  output_tokens_per_day: z.number(),
  input_tokens_per_30d: z.number(),
  output_tokens_per_30d: z.number(),
});
export type Estimate = z.infer<typeof estimateSchema>;

/**
 * `/campaigns/{c}/burn-rate` returns a WHOLE estimate per account, not a
 * summary row. A later task renders the totals and the actuals and links
 * each row into the panel; it must not re-derive a per-account figure from
 * the tree.
 */
export const burnRateSchema = z.object({
  per_account: z.array(
    z.object({
      tracked_account_id: z.uuid(),
      account_id: z.uuid(),
      handle: z.string().nullable(),
      estimate: estimateSchema,
    }),
  ),
  totals: z.object({
    estimated_credits_per_day: z.number().nullable(),
    estimated_credits_per_30d: z.number().nullable(),
    input_tokens_per_day: z.number(),
    output_tokens_per_day: z.number(),
  }),
  // Every actual is nullable and null means the ledger recorded nothing, not
  // that nothing was spent. A zero would tell a tenant their configuration is
  // free when no job has written a figure back yet.
  actuals: z.object({
    since: z.iso.datetime({ offset: true }),
    trailing_7d_credits: z.number().int().nullable(),
    trailing_7d_input_tokens: z.number().int().nullable(),
    trailing_7d_output_tokens: z.number().int().nullable(),
  }),
});
export type BurnRate = z.infer<typeof burnRateSchema>;
